import React, { useState ,useEffect } from 'react';
import { Modal, Button, Form, Spinner, Dropdown } from 'react-bootstrap';
import { X, ExclamationTriangle, InfoCircle, Printer } from 'react-bootstrap-icons';
import { Terminals } from '../../../../../types';
import {createTerminal} from '../../../../../../../../crud/management_api/terminals';
import { toast } from 'react-toastify';
import './CreateTerminal.scss';
import { FaWifi, FaUsb} from 'react-icons/fa6';
import { WorkstationOption , getWorkstationsOptions } from '../../../utils/utils';

interface CreateTerminalProps {
  showCreateModal: boolean;
  setShowCreateModal: (show: boolean) => void;
  setCurrentData: (data: Terminals) => void;
}

const CreateTerminal: React.FC<CreateTerminalProps> = ({
        showCreateModal,
        setShowCreateModal,
        setCurrentData,
      }) => {
        const [step, setStep] = useState(1); // Controla el paso actual
        const [selectedType, setSelectedType] = useState<string | null>(null); // Tipo de terminal seleccionado
        const [workstations, setWorkstations] = useState<WorkstationOption[]>([]);
        const [isCreating, setIsCreating] = useState(false);
        const [ipError, setIpError] = useState('');
        const [showInfoBanner, setShowInfoBanner] = useState(false);
        const [formData, setFormData] = useState({
          terminal_name: '',
          ip_address: '',
          workstation_id: '',
        });

        // IP validation function
        const validateIP = (ip: string): boolean => {
          if (!ip || ip.trim() === '') return true; // Empty is valid for optional field
          
          const trimmedIp = ip.trim();
          
          // IPv4 validation
          const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
          
          // IPv6 validation (more comprehensive)
          const ipv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::1$|^::ffff:[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}$|^::$/;
          
          // Additional IPv6 compressed formats
          const ipv6CompressedRegex = /^([0-9a-fA-F]{1,4}:){1,7}:$|^([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}$|^([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}$|^([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}$|^([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}$|^([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}$|^[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})$/;
          
          return ipv4Regex.test(trimmedIp) || ipv6Regex.test(trimmedIp) || ipv6CompressedRegex.test(trimmedIp);
        };

        const handleIpChange = (value: string) => {
          setFormData((prevData) => ({
            ...prevData,
            ip_address: value,
          }));
          
          if (value.trim() !== '' && !validateIP(value)) {
            setIpError('Formato de IP inválido. Ingrese una dirección IPv4 o IPv6 válida.');
          } else {
            setIpError('');
          }
        };
      
        useEffect(() => {
          getWorkstationsOptions().then((response) => {
            if (response) {
              setWorkstations(response);
            }
          });
        }, []);
      
        const handleNext = () => {
          if (!selectedType) return;
          setShowInfoBanner(selectedType === 'USB'); // Show banner for USB printers
          setStep(2);
        };
      
        const handleBack = () => {
          setStep(1);
          setSelectedType(null);
          setShowInfoBanner(false);
          setIpError('');
        };
      
        const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
          const { name, value } = e.target;
          if (name === 'ip_address') {
            handleIpChange(value);
          } else {
            setFormData((prevData) => ({
              ...prevData,
              [name]: value,
            }));
          }
        };
      
        const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          if (isCreating) return; // Prevent double submissions

          // Validate IP if provided for IP printers
          if (selectedType === 'IP' && formData.ip_address.trim() !== '' && !validateIP(formData.ip_address)) {
            setIpError('Formato de IP inválido. Ingrese una dirección IPv4 o IPv6 válida.');
            return;
          }

          setIsCreating(true);
          
          const userContext = localStorage.getItem('user');
          const localId = userContext != null ? JSON.parse(userContext).local_id : 0;
          const ipAddress = formData.ip_address === '' ? null : formData.ip_address; // Si no es IP, no requiere dirección
          const newTerminal: Terminals = {
            terminal_id: '',
            terminal_name: formData.terminal_name,
            terminal_type_id: selectedType === 'IP' ? 1 : selectedType === 'USB' ? 8 : 9, // Asigna el ID según el tipo
            workstation_id: formData.workstation_id,
            ip_address: selectedType === 'IP' ? ipAddress : null, // Solo IP requiere dirección
            local_id: localId,
            state: true,
            created_at: '',
          };
      
          createTerminal(newTerminal)
            .then((response) => {
              toast.success('Dispositivo creado con éxito!');
              setShowCreateModal(false);
              const createdTerminal: Terminals = (response as any).data;
              setCurrentData(createdTerminal);
            })
            .catch((error) => {
              console.error('Error al crear una terminal:', error);
              toast.error('Error al crear el dispositivo:\n' + error.response.data.message);
            })
            .finally(() => {
              setIsCreating(false);
            });
        };
      
        return (
          <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} className="create-terminal-modal">
            <Modal.Header>
              <div className="form-modal-title-container">
                <Modal.Title className="form-modal-title">
                  {step === 1 ? 'Seleccionar tipo de dispositivo' : 'Crear dispositivo'}
                </Modal.Title>
              </div>
              <Button variant="none" className="modal-close-button" onClick={() => setShowCreateModal(false)}>
                <X size={24} />
              </Button>
            </Modal.Header>
            <Modal.Body className="modal-body-create-terminal">
              {step === 1 && (
                <div>
                  {/* Info banner similar to CreateCoupon */}
                  <div className="terminal-info-banner">
                    <div className="banner-header" onClick={() => setShowInfoBanner(!showInfoBanner)}>
                      <div className="header-content">
                        <InfoCircle className="header-icon" />
                        <h6 className="header-title">Información sobre dispositivos</h6>
                      </div>
                      <div className={`toggle-icon ${showInfoBanner ? 'expanded' : 'collapsed'}`}>
                        ▼
                      </div>
                    </div>
                    {showInfoBanner && (
                      <div className="banner-content">
                        Seleccione el tipo de dispositivo que desea agregar. Las <strong>impresoras IP</strong> se conectan 
                        a través de la red, mientras que las <strong>impresoras USB</strong> se conectan directamente al equipo.
                      </div>
                    )}
                  </div>

                  {/* Printer type selection with CreateCoupon styling */}
                  <div className="form-section printer-type-section">
                    <div className="section-title">
                      <Printer className="section-icon" />
                      Tipo de dispositivo
                    </div>
                    <div className="type-selection">
                      <div
                        className={`type-option ${selectedType === 'IP' ? 'active' : ''}`}
                        onClick={() => setSelectedType('IP')}
                      >
                        <div className="type-header">
                          <div className="type-icon"><FaWifi /></div>
                          <div className="type-title">Impresora por Dirección IP</div>
                        </div>
                        <div className="type-description">Conéctate a una impresora de red usando su dirección IP.</div>
                      </div>
                      <div
                        className={`type-option ${selectedType === 'USB' ? 'active' : ''}`}
                        onClick={() => setSelectedType('USB')}
                      >
                        <div className="type-header">
                          <div className="type-icon"><FaUsb /></div>
                          <div className="type-title">Impresora por USB</div>
                        </div>
                        <div className="type-description">Conecta una impresora directamente al equipo mediante USB.</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {step === 2 && (
                <div>
                  {/* USB Info Banner */}
                  {selectedType === 'USB' && (
                    <div className="usb-warning-banner">
                      <div className="banner-header">
                        <div className="header-content">
                          <Printer className="header-icon" />
                          <div className="header-text">
                            <div className="header-title">Importante para impresoras USB</div>
                            <div className="header-subtitle">Configuración requerida</div>
                          </div>
                        </div>
                      </div>
                      <div className="banner-content">
                        Para que la impresora USB funcione correctamente, <strong>el nombre que ingrese aquí debe coincidir 
                        exactamente</strong> con el nombre de la impresora configurada en el sistema operativo del equipo.
                        <br/><br/>
                        Ejemplo: Si la impresora se llama "EPSON TM-T20III" en Windows, use exactamente ese nombre.
                      </div>
                    </div>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <div className="form-field">
                      <label className="field-label">Nombre</label>
                      <Form.Control
                        type="text"
                        name="terminal_name"
                        placeholder="Nombre del dispositivo"
                        value={formData.terminal_name}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    {selectedType === 'IP' && (
                      <div className="form-field">
                        <label className="field-label">Dirección IP</label>
                        <Form.Control
                          type="text"
                          name="ip_address"
                          placeholder="Ej: 192.168.1.100 o 2001:db8::1"
                          value={formData.ip_address}
                          onChange={handleFormChange}
                          className={ipError ? 'is-invalid' : ''}
                          required
                        />
                      </div>
                    )}

                    {ipError && (
                      <div className="warning-banner">
                        <ExclamationTriangle className="warning-icon" />
                        <div className="warning-content">
                          <div className="warning-title">Formato de IP inválido</div>
                          <div className="warning-message">{ipError}</div>
                        </div>
                      </div>
                    )}

                    <div className="form-field">
                      <label className="field-label">Estación de trabajo</label>
                      <Dropdown>
                        <Dropdown.Toggle variant="none" className="dropdown-toggle">
                          {workstations.find((w) => String(w.id) === String(formData.workstation_id))?.name || 'Seleccione una estación'}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {workstations.map((workstation) => (
                            <Dropdown.Item
                              key={workstation.id}
                              onClick={() => setFormData((prev) => ({ ...prev, workstation_id: String(workstation.id) }))}
                            >
                              {workstation.name}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </Form>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <div className="form-modal-footer-custom">
                {step === 1 && (
                  <Button 
                    className="form-button-create" 
                    onClick={handleNext}
                    disabled={!selectedType}
                  >
                    Siguiente
                  </Button>
                )}
                {step === 2 && (
                  <>
                    <Button 
                      className="form-button-cancel" 
                      onClick={handleBack}
                      disabled={isCreating}
                    >
                      Atrás
                    </Button>
                    <Button 
                      className="form-button-create" 
                      type="submit" 
                      onClick={(e) => { e.preventDefault(); handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>); }}
                      disabled={isCreating || !!ipError}
                    >
                      {isCreating ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Creando...
                        </>
                      ) : (
                        'Crear'
                      )}
                    </Button>
                  </>
                )}
              </div>
            </Modal.Footer>
          </Modal>
        );
      };


export default CreateTerminal;

